// ============================================================================
// TABLERO DE REPARTO — la caja da de alta el pedido (folio + monto + zona + pago) y lo ve pasar por
// Listos → En camino → Entregados. El repartidor lo toma desde SU enlace (página /repartidor/:token);
// si no trae celular, la caja lo asigna aquí. Sondeo cada 10 s. Módulo cross `delivery` (23-oct-2026).
// ============================================================================
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Check, Clock, MapPin, Phone, Search, Undo2, X, PackageCheck, Bike, Loader2, Lock, Pencil, Store, Wallet, Maximize2, Minimize2, Timer, History, ArrowLeftRight } from 'lucide-react';
import Layout from '../../../components/layout/Layout';
import { usePromptDialog, useConfirmDialog } from '../../../components/common/ConfirmDialog';
import { useAuth } from '../../../context/AuthContext';
import { hasModule } from '../../../utils/modulePermissions';
import DeliveryNav from '../components/DeliveryNav';
import { deliveryApi, money, hora, minutos, PAY_LABEL, type Board, type Order, type OrderKind, type OrderDetail, type Payment, type Courier, type Zone } from '../api';

const PAGOS: Payment[] = ['efectivo', 'tarjeta', 'transferencia', 'pagado'];
const WIDE_KEY = 'lealify.repartos.wide';   // pantalla amplia (sin menú lateral), recuerdo por navegador
const leerWide = () => { try { return localStorage.getItem(WIDE_KEY) === '1'; } catch { return false; } };
const err = (e: unknown) => (e instanceof Error ? e.message : 'Algo salió mal');
const inputCls = 'mt-0.5 w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content';
const chip = (on: boolean) => `rounded-lg border px-2.5 py-1.5 text-sm font-medium transition ${
  on ? 'border-accent bg-accent/10 text-accent' : 'border-line bg-surface text-content-2 hover:border-content-3'}`;

function Dot({ color }: { color: string | null }) {
  return <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color || '#94a3b8' }} />;
}

export default function DeliveryBoardPage() {
  const { currentUser } = useAuth();
  const puedeOperar = hasModule(currentUser, 'delivery', 'operate');
  const { prompt, dialog } = usePromptDialog();
  const { confirm, dialog: dialogConfirmar } = useConfirmDialog();   // «¿los datos son correctos?» (flag confirm_before_save)
  const [board, setBoard] = useState<Board | null>(null);
  const [busca, setBusca] = useState('');
  // Alta rápida
  const [ref, setRef] = useState('');
  const [amount, setAmount] = useState('');
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [fee, setFee] = useState('');
  const [courtesy, setCourtesy] = useState(false);   // envío de cortesía: sin envío ni comisión
  const [pay, setPay] = useState<Payment>('efectivo');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [sugeridos, setSugeridos] = useState<string[]>([]);
  const [cliente, setCliente] = useState<{ name: string; balance: number } | null>(null);
  // Fondo de cambio del día por repartidor (Carlo 28-oct 10:22: «no veo la opción para agregarlo»: estaba solo en el Corte).
  const [fondos, setFondos] = useState<Record<number, number>>({});
  // Ajustes Carlo 28-oct (Cenizas), todos detrás de flags del negocio (board.settings): pickup = el cliente recoge (sin zona ni
  // envío); dirElegida = con `address_confirm`, hay que elegir un domicilio guardado o marcar «Domicilio nuevo» antes de guardar;
  // feeOtro = en la captura `phone_first` el envío es un desplegable (zonas · cortesía · «Otro…» escribe el importe).
  const [pickup, setPickup] = useState(false);
  const [dirElegida, setDirElegida] = useState<'none' | 'nuevo' | 'guardado'>('none');
  const [feeOtro, setFeeOtro] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ultimo, setUltimo] = useState<string | null>(null);
  const [asignando, setAsignando] = useState<number | null>(null);
  // Corrección en línea (Carlo 26-oct: «modificar cantidades y folios en los pedidos registrados»): folio, monto, envío,
  // pago, cliente y domicilio en cualquier etapa hasta cerrar el corte. Los productos se corrigen en su punto de venta.
  const [editando, setEditando] = useState<number | null>(null);
  const [ed, setEd] = useState({ external_ref: '', amount: '', delivery_fee: '', payment: 'efectivo' as Payment, customer_name: '', address: '', courtesy: false,
    kind: 'delivery' as OrderKind, zone_id: null as number | null });
  const [edSaving, setEdSaving] = useState(false);
  // Carlo 1-nov (flag board_extras): pantalla amplia (oculta el menú lateral), ficha del pedido al tocar el folio.
  const [wide, setWide] = useState(leerWide);
  const [detalle, setDetalle] = useState<OrderDetail | null>(null);
  const [detalleDe, setDetalleDe] = useState<number | null>(null);
  const refInput = useRef<HTMLInputElement>(null);
  const phoneInput = useRef<HTMLInputElement>(null);

  const load = useCallback(() => deliveryApi.board().then((b) => {
    setBoard(b);
    deliveryApi.funds(b.date).then((f) => setFondos(Object.fromEntries(f.funds.map((x) => [x.courier_id, x.amount])))).catch(() => {});
  }).catch((e) => { if (!board) toast.error(err(e)); }), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 10000);
    return () => window.clearInterval(t);
  }, [load]);

  const layout = board?.settings.form_layout === 'phone_first' ? 'phone_first' : 'classic';
  const confirmarDir = !!board?.settings.address_confirm;
  const pickupOn = !!board?.settings.pickup_enabled;
  const cashbackOn = board?.settings.cashback_enabled !== false; // apagado por negocio (Cenizas): no se muestra saldo ni «sin cashback»
  const extras = !!board?.settings.board_extras;
  const alternarWide = () => { const v = !wide; setWide(v); try { localStorage.setItem(WIDE_KEY, v ? '1' : '0'); } catch { /* sin almacenamiento */ } };
  const abrirDetalle = async (o: Order) => {
    setDetalleDe(o.id); setDetalle(null);
    try { setDetalle(await deliveryApi.detail(o.id)); } catch (e) { toast.error(err(e)); setDetalleDe(null); }
  };
  const cerrarDetalle = () => { setDetalleDe(null); setDetalle(null); };
  useEffect(() => {
    if (detalleDe === null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cerrarDetalle(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detalleDe]);
  const zonaDefault = (b: Board | null): Zone | null => {
    const id = b?.settings.default_zone_id;
    return (id && b?.zones.find((z) => z.id === id && z.active)) || null;
  };
  // Zona preseleccionada (flag `default_zone_id`): al cargar el tablero la primera vez y tras cada guardado.
  const zonaAplicada = useRef(false);
  useEffect(() => {
    if (!board || zonaAplicada.current) return;
    zonaAplicada.current = true;
    const z = zonaDefault(board);
    if (z && zoneId === null && fee === '' && !courtesy) { setZoneId(z.id); setFee(String(z.fee)); }
  }, [board]); // eslint-disable-line react-hooks/exhaustive-deps

  // Teléfono completo → nombre y saldo del cliente + sus últimos domicilios (hasta 5).
  useEffect(() => {
    const d = phone.replace(/\D/g, '');
    if (d.length !== 10) { setCliente(null); setSugeridos([]); setDirElegida('none'); return; }
    let vivo = true;
    deliveryApi.customer(d).then((r) => {
      if (!vivo) return;
      setCliente(r.contact ? { name: r.contact.name, balance: r.contact.balance } : null);
      // El nombre viaja con el pedido (con el cashback apagado no hay contacto de lealtad: el nombre vive en los pedidos).
      if (r.contact && r.contact.name) setName(r.contact.name);
      setSugeridos(r.addresses);
      setDirElegida('none');
      // Con «confirmar domicilio» NO se rellena solo: la caja tiene que elegirlo (así el domicilio coincide con el del ticket).
      if (!confirmarDir && !address && r.addresses[0]) setAddress(r.addresses[0]);
    }).catch(() => {});
    return () => { vivo = false; };
  }, [phone]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = board?.settings.external_ref_label || 'Folio';
  const elegirZona = (id: number) => {
    const z = board?.zones.find((x) => x.id === id);
    if (zoneId === id) { setZoneId(null); setFee(''); return; }
    setZoneId(id); setCourtesy(false); if (z) setFee(String(z.fee));
  };
  const alternarCortesia = () => {
    if (courtesy) { setCourtesy(false); setFee(zoneId ? String(board?.zones.find((x) => x.id === zoneId)?.fee ?? '') : ''); return; }
    setCourtesy(true); setFee('0');
  };

  // Envío como desplegable (captura phone_first): zona · cortesía · otro importe.
  const envioValor = courtesy ? 'cortesia' : feeOtro ? 'otro' : zoneId !== null ? String(zoneId) : '';
  const elegirEnvio = (v: string) => {
    if (v === 'cortesia') { setFeeOtro(false); setZoneId(null); setCourtesy(true); setFee('0'); return; }
    if (v === 'otro') { setCourtesy(false); setZoneId(null); setFeeOtro(true); setFee(''); return; }
    setFeeOtro(false); setCourtesy(false);
    if (v === '') { setZoneId(null); setFee(''); return; }
    const z = board?.zones.find((x) => String(x.id) === v);
    setZoneId(z ? z.id : null); setFee(z ? String(z.fee) : '');
  };
  const elegirDomicilio = (a: string) => { setAddress(a); setDirElegida('guardado'); };
  const domicilioNuevo = () => { setAddress(''); setDirElegida('nuevo'); };
  const limpiar = () => {
    const z = zonaDefault(board);
    setRef(''); setAmount(''); setZoneId(z ? z.id : null); setFee(z ? String(z.fee) : ''); setCourtesy(false); setFeeOtro(false); setPay('efectivo');
    setAddress(''); setPhone(''); setName(''); setCliente(null); setSugeridos([]); setDirElegida('none'); setPickup(false);
  };

  const guardar = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (saving) return;
    const monto = Number(amount);
    if (!(monto > 0)) { toast.error('Captura el monto'); return; }
    if (!ref.trim() && board?.settings.external_ref_required) { toast.error(`Captura el ${label}`); refInput.current?.focus(); return; }
    if (confirmarDir && !pickup && sugeridos.length > 0 && dirElegida === 'none') { toast.error('Confirma el domicilio: elige uno de los guardados o «Domicilio nuevo»'); return; }
    // Ventana de confirmación (Carlo 28-oct 12:17, su esposa captura): los datos concentrados antes de guardar. Enter = sí, Esc = corregir.
    if (board?.settings.confirm_before_save) {
      const z = board.zones.find((x) => x.id === zoneId);
      const tel = phone.replace(/\D/g, '');
      const filas: [string, string][] = [
        [label, ref.trim() || '—'],
        ['Cliente', [name.trim() || cliente?.name || '', tel].filter(Boolean).join(' · ') || 'sin teléfono'],
        ['Domicilio', pickup ? `Recoge en el local${address.trim() ? ` · ${address.trim()}` : ''}` : (address.trim() || '—')],
        ['Total', money(monto)],
        ['Envío', pickup ? 'Pickup · sin envío' : courtesy ? 'Cortesía · sin envío' : `${z ? `${z.name} · ` : ''}${money(Number(fee) || 0)}`],
        ['Pago', PAY_LABEL[pay]],
      ];
      const ok = await confirm({
        title: '¿Los datos son correctos?', confirmLabel: 'Sí, guardar', cancelLabel: 'Corregir', keyboard: true,
        icon: <Check size={20} className="mr-2.5 flex-shrink-0 text-emerald-600" />,
        message: (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            {filas.map(([k, v]) => <Fragment key={k}><dt className="text-content-3">{k}</dt><dd className="font-medium text-content">{v}</dd></Fragment>)}
          </dl>
        ),
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const r = await deliveryApi.create(pickup
        ? { external_ref: ref.trim(), amount: monto, kind: 'pickup', payment: pay, address: address.trim() || null, customer_phone: phone.replace(/\D/g, '') || null, customer_name: name.trim() || null }
        : { external_ref: ref.trim(), amount: monto, zone_id: zoneId, delivery_fee: courtesy ? 0 : fee === '' ? undefined : Number(fee), courtesy,
          payment: pay, address: address.trim() || null, customer_phone: phone.replace(/\D/g, '') || null, customer_name: name.trim() || null });
      const l = r.loyalty;
      const cb = l.ok && l.points_added ? ` · cashback ${money(l.points_added)}${l.contact_name ? ` a ${l.contact_name}` : ''}` : '';
      const aviso = l.skipped && l.reason === 'folio_ya_usado' ? ' · sin cashback: ese folio ya se registró en lealtad'
        : l.skipped && l.reason === 'tope' ? ' · sin cashback: el plan llegó al tope de clientes'
        : l.skipped && !['sin_telefono', 'lealtad_apagada', 'cashback_apagado'].includes(l.reason || '') ? ' · sin cashback' : '';
      setUltimo(`✓ ${r.order.external_ref} · ${money(r.order.amount)}${r.order.kind === 'pickup' ? ' · pickup' : ''}${cb}${aviso}`);
      limpiar();
      (layout === 'phone_first' ? phoneInput : refInput).current?.focus();
      load();
    } catch (e2) { toast.error(err(e2)); }
    finally { setSaving(false); }
  };

  const accion = async (fn: () => Promise<unknown>, ok?: string) => {
    try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { toast.error(err(e)); load(); }
  };
  const capturarFondo = async (c: Courier) => {
    if (!board) return;
    const v = await prompt({ title: `Fondo de cambio de ${c.name}`, message: 'Efectivo que se lleva al empezar el día. Entra en su corte: lo entrega junto con lo cobrado.',
      placeholder: '$200', defaultValue: fondos[c.id] ? String(fondos[c.id]) : '', confirmLabel: 'Guardar' });
    if (v === null) return;
    const n = Number(String(v).replace(/[^\d.]/g, '')) || 0;
    await accion(() => deliveryApi.setFund(c.id, n, board.date), n ? `Fondo de ${c.name}: ${money(n)}` : `Fondo de ${c.name} en $0`);
  };
  const cancelar = async (o: Order) => {
    const reason = await prompt({ title: `Cancelar el pedido ${o.external_ref}`, message: o.loyalty_tx_id ? 'Se le quita el cashback al cliente.' : undefined,
      placeholder: 'Motivo (p. ej. el cliente ya no lo quiso)', confirmLabel: 'Cancelar pedido', required: true });
    if (reason && reason.trim().length >= 3) accion(() => deliveryApi.cancel(o.id, reason.trim()), 'Pedido cancelado');
    else if (reason !== null) toast.error('Escribe el motivo');
  };

  const abrirEdicion = (o: Order) => {
    setAsignando(null); setEditando(o.id);
    setEd({ external_ref: o.external_ref, amount: String(o.amount), delivery_fee: String(o.delivery_fee), payment: o.payment,
      customer_name: o.customer_name || '', address: o.address || '', courtesy: !!o.courtesy, kind: o.kind, zone_id: o.zone_id });
  };
  // Cambiar el tipo de entrega en la corrección (Carlo 1-nov): a pickup limpia zona/envío; a domicilio propone la zona preseleccionada.
  const cambiarTipoEd = (k: OrderKind, o: Order) => {
    if (k === ed.kind) return;
    if (k === 'pickup') { setEd({ ...ed, kind: k, zone_id: null, delivery_fee: '0', courtesy: false }); return; }
    const z = o.zone_id ? board?.zones.find((x) => x.id === o.zone_id) : zonaDefault(board);
    setEd({ ...ed, kind: k, zone_id: z ? z.id : null, delivery_fee: o.kind === 'delivery' ? String(o.delivery_fee) : z ? String(z.fee) : '' });
  };
  const guardarEdicion = async (o: Order) => {
    if (edSaving) return;
    const monto = Number(ed.amount);
    if (!(monto > 0)) { toast.error('Captura el monto'); return; }
    if (!ed.external_ref.trim() && board?.settings.external_ref_required) { toast.error(`Captura el ${label}`); return; }
    const cambios: Partial<Order> = {};
    if (ed.external_ref.trim() !== o.external_ref) cambios.external_ref = ed.external_ref.trim();
    if (monto !== o.amount) cambios.amount = monto;
    if (ed.kind !== o.kind) {
      cambios.kind = ed.kind;
      if (ed.kind === 'delivery') { cambios.zone_id = ed.zone_id; cambios.delivery_fee = Number(ed.delivery_fee || 0); }
    } else if (ed.kind === 'delivery' && ed.zone_id !== o.zone_id) { cambios.zone_id = ed.zone_id; cambios.delivery_fee = Number(ed.delivery_fee || 0); }
    if (ed.kind === 'delivery' && ed.courtesy !== !!o.courtesy) cambios.courtesy = ed.courtesy;
    if (ed.kind === 'delivery' && !ed.courtesy && Number(ed.delivery_fee || 0) !== o.delivery_fee) cambios.delivery_fee = Number(ed.delivery_fee || 0);
    if (ed.payment !== o.payment) cambios.payment = ed.payment;
    if (ed.customer_name.trim() !== (o.customer_name || '')) cambios.customer_name = ed.customer_name.trim() || null;
    if (ed.address.trim() !== (o.address || '')) cambios.address = ed.address.trim() || null;
    if (!Object.keys(cambios).length) { setEditando(null); return; }
    setEdSaving(true);
    try {
      const r = await deliveryApi.update(o.id, cambios);
      const l = r.loyalty;
      const tipo = cambios.kind ? (cambios.kind === 'pickup' ? ' · ahora lo recoge el cliente' : ' · ahora va a domicilio') : '';
      toast.success(`Pedido ${r.order.external_ref} corregido${tipo}${l && l.ok && l.points_added ? ` · cashback ajustado a ${money(l.points_added)}` : ''}`);
      setEditando(null); load();
    } catch (e2) { toast.error(err(e2)); }
    finally { setEdSaving(false); }
  };
  // Reporte de pago del repartidor (Carlo 1-nov): la caja acepta (cambia el pago del pedido) o lo deja como estaba.
  const resolverReporte = (o: Order, decision: 'accept' | 'dismiss') => accion(
    () => deliveryApi.update(o.id, { payment_report: decision }),
    decision === 'accept'
      ? `Pago del ${o.external_ref} cambiado a ${PAY_LABEL[o.payment_reported || o.payment].toLowerCase()}`
      : `El ${o.external_ref} se queda como ${PAY_LABEL[o.payment].toLowerCase()}`);
  // Línea de tiempos de la tarjeta (flag board_extras): capturado → salió → entregado, con los minutos.
  const tiemposDe = (o: Order) => {
    const partes = [`Capt. ${hora(o.created_at)}`];
    if (o.taken_at) partes.push(`salió ${hora(o.taken_at)}`);
    if (o.delivered_at) partes.push(`entregado ${hora(o.delivered_at)}`);
    const total = minutos(o.created_at, o.delivered_at);
    const ruta = minutos(o.taken_at, o.delivered_at);
    const enRuta = o.status === 'en_camino' && o.taken_at ? minutos(o.taken_at, new Date().toISOString()) : null;
    const resumen = o.status === 'entregado' && total != null ? ` · ${total} min${ruta != null && o.kind !== 'pickup' ? ` (${ruta} en ruta)` : ''}`
      : enRuta != null ? ` · ${enRuta} min en ruta` : '';
    return partes.join(' → ') + resumen;
  };

  const filtra = useMemo(() => {
    const s = busca.trim().toLowerCase();
    if (!s) return (l: Order[]) => l;
    return (l: Order[]) => l.filter((o) => [o.external_ref, o.address, o.customer_name, o.customer_phone, o.courier_name]
      .some((x) => (x || '').toLowerCase().includes(s)));
  }, [busca]);

  const couriers: Courier[] = board?.couriers || [];
  const cerrado = !!board?.closed;

  const edicion = (o: Order) => (
    <li key={o.id} className="rounded-xl border border-accent bg-surface p-3 shadow-sm">
      <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-content"><Pencil size={14} />Corregir el pedido {o.external_ref}</p>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <label className="text-xs text-content-2">{label}
          <input value={ed.external_ref} onChange={(e) => setEd({ ...ed, external_ref: e.target.value })} className={inputCls} /></label>
        <label className="text-xs text-content-2">Monto del ticket
          <input value={ed.amount} onChange={(e) => setEd({ ...ed, amount: e.target.value.replace(/[^\d.]/g, '') })} inputMode="decimal" className={inputCls} /></label>
        {/* Tipo de entrega (Carlo 1-nov): se puede cambiar mientras no esté entregado y el negocio tenga pickup. */}
        {pickupOn && o.status !== 'entregado' && (
          <div className="col-span-2">
            <span className="mb-0.5 block text-xs text-content-2"><ArrowLeftRight size={11} className="mr-1 inline" />Tipo de entrega</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => cambiarTipoEd('delivery', o)} className={chip(ed.kind === 'delivery')}><Bike size={13} className="mr-1 inline" />A domicilio</button>
              <button type="button" onClick={() => cambiarTipoEd('pickup', o)} className={chip(ed.kind === 'pickup')}><Store size={13} className="mr-1 inline" />Recoge en el local</button>
            </div>
            {ed.kind !== o.kind && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                {ed.kind === 'pickup' ? `Se quita el envío${o.courier_id ? ' y el repartidor; el pedido regresa a Listos' : ''}. El cliente pasa por él.` : 'Elige la zona y el costo de envío; el pedido queda en Listos para que lo tome un repartidor.'}
              </p>
            )}
          </div>
        )}
        {ed.kind === 'pickup' ? (
          <p className="flex items-center gap-1 self-end pb-1.5 text-xs text-content-3"><Store size={12} />Pickup: sin envío</p>
        ) : (
          <>
            {(ed.kind !== o.kind || o.kind === 'delivery') && pickupOn && (
              <label className="text-xs text-content-2">Zona
                <select value={ed.zone_id ?? ''} onChange={(e) => { const z = board?.zones.find((x) => String(x.id) === e.target.value); setEd({ ...ed, zone_id: z ? z.id : null, delivery_fee: z ? String(z.fee) : ed.delivery_fee }); }} className={inputCls}>
                  <option value="">Sin zona</option>
                  {board?.zones.map((z) => <option key={z.id} value={String(z.id)}>{z.name} · {money(z.fee)}</option>)}
                </select></label>
            )}
            <label className="text-xs text-content-2">Costo de envío
              <input value={ed.courtesy ? '0' : ed.delivery_fee} disabled={ed.courtesy} onChange={(e) => setEd({ ...ed, delivery_fee: e.target.value.replace(/[^\d.]/g, '') })} inputMode="decimal" className={`${inputCls} disabled:opacity-60`} /></label>
          </>
        )}
        {ed.kind !== 'pickup' && (
          <label className="col-span-2 flex items-center gap-1.5 text-xs text-content-2">
            <input type="checkbox" checked={ed.courtesy} onChange={(e) => setEd({ ...ed, courtesy: e.target.checked, delivery_fee: e.target.checked ? '0' : ed.delivery_fee })} />
            Sin envío (cortesía): no se cobra envío y el repartidor no lleva comisión</label>
        )}
        <label className="text-xs text-content-2">Pago
          <select value={ed.payment} onChange={(e) => setEd({ ...ed, payment: e.target.value as Payment })} className={inputCls}>
            {PAGOS.map((p) => <option key={p} value={p}>{PAY_LABEL[p]}</option>)}
          </select></label>
        <label className="text-xs text-content-2">Cliente
          <input value={ed.customer_name} onChange={(e) => setEd({ ...ed, customer_name: e.target.value })} className={inputCls} /></label>
        <label className="col-span-2 text-xs text-content-2">Domicilio
          <input value={ed.address} onChange={(e) => setEd({ ...ed, address: e.target.value })} className={inputCls} /></label>
      </div>
      {o.loyalty_tx_id && <p className="mt-1.5 text-xs text-content-3">Si cambias el monto, el cashback del cliente se recalcula.</p>}
      <div className="mt-2 flex gap-2">
        <button disabled={edSaving} onClick={() => guardarEdicion(o)}
          className="flex items-center gap-1 rounded-lg bg-accent-solid px-2.5 py-1 text-sm font-medium text-white hover:bg-accent-solid/90 disabled:bg-line">
          {edSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}Guardar</button>
        <button onClick={() => setEditando(null)} className="rounded-lg border border-line px-2.5 py-1 text-sm text-content-2 hover:text-content">Cancelar</button>
      </div>
    </li>
  );

  // Semáforo por tiempo de espera (Carlo 3-nov, petición #1638 de su cajera; flag board_extras): mientras el pedido
  // sigue abierto (listo o en camino) la tarjeta se pinta según los minutos desde que se capturó: verde claro <20,
  // amarillo 20-34, rojo 35+. Entregados y cancelados sin color. El tablero recarga cada 10 s, así que cambia solo.
  const semaforo = (o: Order): { cls: string; min: number } | null => {
    if (!extras || (o.status !== 'listo' && o.status !== 'en_camino')) return null;
    const min = minutos(o.created_at, new Date().toISOString());
    if (min == null) return null;
    if (min >= 35) return { min, cls: 'border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/15' };
    if (min >= 20) return { min, cls: 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/15' };
    return { min, cls: 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/30 dark:bg-emerald-500/10' };
  };

  const tarjeta = (o: Order) => editando === o.id ? edicion(o) : (
    <li key={o.id} className={`rounded-xl border p-3 shadow-sm ${semaforo(o)?.cls ?? 'border-line bg-surface'}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            {extras ? (
              <button type="button" onClick={() => abrirDetalle(o)} title="Ver la ficha del pedido y del cliente"
                className="font-mono text-base font-bold text-content underline decoration-dotted underline-offset-4 hover:text-accent">{o.external_ref}</button>
            ) : <span className="font-mono text-base font-bold text-content">{o.external_ref}</span>}
            <span className="text-base font-semibold text-content">{money(o.amount)}</span>
            <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${o.payment === 'efectivo' ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
              : o.payment === 'pagado' ? 'bg-slate-500/10 text-content-2' : 'bg-sky-500/10 text-sky-700 dark:text-sky-400'}`}>{PAY_LABEL[o.payment]}</span>
          </div>
          {o.address && <p className="mt-1 flex items-start gap-1 text-sm text-content-2"><MapPin size={14} className="mt-0.5 shrink-0" />{o.address}</p>}
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-content-3">
            {o.kind === 'pickup' && <span className="flex items-center gap-1 rounded-md bg-sky-500/10 px-1.5 py-0.5 font-semibold text-sky-700 dark:text-sky-300"><Store size={11} />Pickup · recoge en el local</span>}
            {o.courtesy && <span className="rounded-md bg-violet-500/10 px-1.5 py-0.5 font-semibold text-violet-700 dark:text-violet-300">Cortesía · sin envío</span>}
            {o.zone_is_foreign && <span className="rounded-md bg-orange-500/15 px-1.5 py-0.5 font-semibold text-orange-700 dark:text-orange-300">Foráneo</span>}
            {o.zone_name && !o.courtesy && <span>{o.zone_name} · envío {money(o.delivery_fee)}</span>}
            {o.zone_name && o.courtesy && <span>{o.zone_name}</span>}
            {o.customer_name && <span>{o.customer_name}</span>}
            {o.customer_phone && <span className="flex items-center gap-0.5"><Phone size={11} />{o.customer_phone}</span>}
            {!extras && <span className="flex items-center gap-0.5"><Clock size={11} />{hora(o.status === 'en_camino' ? o.taken_at : o.status === 'entregado' ? o.delivered_at : o.created_at)}</span>}
          </p>
          {/* Tiempos (Carlo 1-nov, flag board_extras): capturado → salió → entregado y los minutos. */}
          {extras && <p className="mt-1 flex items-center gap-1 text-xs text-content-3"><Timer size={11} />{tiemposDe(o)}</p>}
          {o.status !== 'listo' && o.courier_name && (
            <p className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-content"><Dot color={o.courier_color} />{o.courier_name}</p>
          )}
          {/* Reporte de pago del repartidor (Carlo 1-nov): la caja valida; mientras, el corte sigue con el pago capturado. */}
          {o.payment_reported && o.payment_reported !== o.payment && (
            <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-800 dark:text-amber-300">
              <p><b>{o.courier_name ? o.courier_name.split(' ')[0] : 'El repartidor'}</b> reporta que el cliente pagó con <b>{PAY_LABEL[o.payment_reported].toLowerCase()}</b>
                {o.payment_reported_at ? ` (${hora(o.payment_reported_at)})` : ''}; está capturado como {PAY_LABEL[o.payment].toLowerCase()}.</p>
              {puedeOperar && !cerrado && (
                <div className="mt-1.5 flex gap-1.5">
                  <button onClick={() => resolverReporte(o, 'accept')} className="rounded-md bg-amber-600 px-2 py-1 font-semibold text-white hover:bg-amber-700">Aceptar: {PAY_LABEL[o.payment_reported]}</button>
                  <button onClick={() => resolverReporte(o, 'dismiss')} className="rounded-md border border-amber-600/40 px-2 py-1 font-medium hover:bg-amber-500/10">Dejar {PAY_LABEL[o.payment].toLowerCase()}</button>
                </div>
              )}
            </div>
          )}
        </div>
        {puedeOperar && !cerrado && o.status !== 'cancelado' && (
          <button onClick={() => abrirEdicion(o)} title="Corregir pedido" className="rounded-md p-1 text-content-3 hover:bg-surface-2 hover:text-accent"><Pencil size={16} /></button>
        )}
        {puedeOperar && !cerrado && o.status !== 'cancelado' && (
          <button onClick={() => cancelar(o)} title="Cancelar pedido" className="rounded-md p-1 text-content-3 hover:bg-surface-2 hover:text-red-600"><X size={16} /></button>
        )}
      </div>
      {puedeOperar && !cerrado && o.status === 'listo' && o.kind === 'pickup' && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {/* Aviso «lista para recoger» (Carlo 28-oct 10:44): solo con el flag y teléfono; una vez por pedido. */}
          {board?.settings.pickup_ready_notice && o.customer_phone && (o.notified_ready_at
            ? <span className="flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300"><Check size={12} />Avisado {hora(o.notified_ready_at)}</span>
            : <button onClick={() => accion(() => deliveryApi.ready(o.id), 'Aviso enviado: lista para recoger')}
                className="flex items-center gap-1 rounded-lg border border-accent/40 bg-accent/10 px-2.5 py-1 text-sm font-medium text-accent hover:bg-accent/20"><Phone size={13} />Avisar: lista para recoger</button>)}
          <button onClick={() => accion(() => deliveryApi.deliver(o.id), 'Entregado al cliente')}
            className="flex items-center gap-1 rounded-lg bg-accent-solid px-2.5 py-1 text-sm font-medium text-white hover:bg-accent-solid/90"><Check size={14} />Entregado (recogió)</button>
        </div>
      )}
      {puedeOperar && !cerrado && o.status === 'listo' && o.kind !== 'pickup' && (
        asignando === o.id ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {couriers.map((c) => (
              <button key={c.id} onClick={() => { setAsignando(null); accion(() => deliveryApi.assign(o.id, c.id), `Asignado a ${c.name}`); }}
                className="flex items-center gap-1.5 rounded-lg border border-line px-2 py-1 text-sm text-content hover:border-accent"><Dot color={c.color} />{c.name}</button>
            ))}
            <button onClick={() => setAsignando(null)} className="px-2 text-sm text-content-3">Cerrar</button>
          </div>
        ) : (
          <button onClick={() => setAsignando(o.id)} className="mt-2 text-sm font-medium text-accent hover:underline">Asignar repartidor</button>
        )
      )}
      {puedeOperar && !cerrado && o.status === 'en_camino' && (
        <div className="mt-2 flex gap-2">
          <button onClick={() => accion(() => deliveryApi.deliver(o.id), 'Entregado')}
            className="flex items-center gap-1 rounded-lg bg-accent-solid px-2.5 py-1 text-sm font-medium text-white hover:bg-accent-solid/90"><Check size={14} />Entregado</button>
          <button onClick={() => accion(() => deliveryApi.unassign(o.id))}
            className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-sm text-content-2 hover:text-content"><Undo2 size={14} />A listos</button>
        </div>
      )}
    </li>
  );

  const columna = (titulo: string, icono: React.ReactNode, lista: Order[], vacio: string, tono: string) => (
    <section className="flex min-w-0 flex-col rounded-2xl bg-surface-2/70 p-3">
      <h2 className="mb-2 flex items-center gap-2 px-1 text-sm font-semibold text-content">
        <span className={tono}>{icono}</span>{titulo}
        <span className="ml-auto rounded-full bg-surface px-2 py-0.5 text-xs text-content-2">{lista.length}</span>
      </h2>
      {lista.length ? <ul className="space-y-2">{lista.map(tarjeta)}</ul>
        : <p className="px-1 py-6 text-center text-sm text-content-3">{vacio}</p>}
    </section>
  );

  const totalHoy = board ? [...board.en_camino, ...board.entregados].reduce((a, o) => a + o.amount, 0) : 0;

  // Ficha del pedido (Carlo 1-nov): cliente, ticket, tiempos e historial del cliente por teléfono.
  const fichaPedido = detalleDe !== null && (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={cerrarDetalle}>
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-surface p-4 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        {!detalle ? <div className="flex justify-center py-10 text-content-3"><Loader2 className="animate-spin" /></div> : (() => {
          const o = detalle.order; const h = detalle.historial; const t = detalle.tiempos;
          const fila = (k: string, v: React.ReactNode) => <Fragment key={k}><dt className="text-content-3">{k}</dt><dd className="font-medium text-content">{v}</dd></Fragment>;
          return (
            <>
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-content-3">{label}</p>
                  <p className="font-mono text-2xl font-bold text-content">{o.external_ref} <span className="font-sans text-lg font-semibold">{money(o.amount)}</span></p>
                </div>
                <button onClick={cerrarDetalle} className="rounded-md p-1 text-content-3 hover:bg-surface-2 hover:text-content" aria-label="Cerrar"><X size={18} /></button>
              </div>
              <h3 className="mb-1 text-sm font-semibold text-content">Ticket</h3>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                {fila('Pago', <>{PAY_LABEL[o.payment]}{o.payment_reported && o.payment_reported !== o.payment ? <span className="ml-1 text-amber-700 dark:text-amber-300">· repartidor reporta {PAY_LABEL[o.payment_reported].toLowerCase()}</span> : null}</>)}
                {fila('Entrega', o.kind === 'pickup' ? 'Recoge en el local' : `A domicilio${o.zone_name ? ` · ${o.zone_name}` : ''}${o.zone_is_foreign ? ' · Foráneo' : ''}${o.courtesy ? ' · Cortesía' : ` · envío ${money(o.delivery_fee)}`}`)}
                {fila('Estado', `${o.status === 'listo' ? 'Listo' : o.status === 'en_camino' ? 'En camino' : o.status === 'entregado' ? 'Entregado' : 'Cancelado'}${o.courier_name ? ` · ${o.courier_name}` : ''}${o.cancel_reason ? ` · ${o.cancel_reason}` : ''}`)}
                {o.note && fila('Nota', o.note)}
              </dl>
              <h3 className="mb-1 mt-3 text-sm font-semibold text-content">Tiempos</h3>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                {fila('Capturado', hora(o.created_at))}
                {o.taken_at && fila('Salió', `${hora(o.taken_at)}${t.preparacion_min != null ? ` · ${t.preparacion_min} min después de capturar` : ''}`)}
                {o.delivered_at && fila('Entregado', `${hora(o.delivered_at)}${t.ruta_min != null && o.kind !== 'pickup' ? ` · ${t.ruta_min} min en ruta` : ''}${t.total_min != null ? ` · ${t.total_min} min en total` : ''}`)}
              </dl>
              <h3 className="mb-1 mt-3 text-sm font-semibold text-content">Cliente</h3>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                {fila('Nombre', o.customer_name || '—')}
                {fila('Teléfono', o.customer_phone ? <a href={`tel:${o.customer_phone}`} className="text-accent hover:underline">{o.customer_phone}</a> : 'sin teléfono')}
                {fila('Domicilio', o.address || (o.kind === 'pickup' ? 'recoge en el local' : '—'))}
              </dl>
              {h && (
                <>
                  <h3 className="mb-1 mt-3 flex items-center gap-1.5 text-sm font-semibold text-content"><History size={14} />Historial del cliente</h3>
                  <p className="text-sm text-content-2">{h.pedidos ? `${h.pedidos} pedido${h.pedidos === 1 ? '' : 's'} anterior${h.pedidos === 1 ? '' : 'es'} · ${money(h.total)}${h.desde ? ` · desde ${h.desde}` : ''}` : 'Es su primer pedido con este teléfono.'}</p>
                  {h.ultimos.length > 0 && (
                    <ul className="mt-1.5 divide-y divide-line rounded-xl border border-line text-sm">
                      {h.ultimos.map((u) => (
                        <li key={u.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                          <span className="text-content-3">{u.business_date}</span>
                          <span className="font-mono text-content">{u.external_ref}</span>
                          <span className="text-xs text-content-3">{u.kind === 'pickup' ? 'pickup' : 'domicilio'}{u.status === 'cancelado' ? ' · cancelado' : ''}</span>
                          <span className="font-semibold text-content">{money(u.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {h.domicilios.length > 1 && <p className="mt-1.5 text-xs text-content-3">Domicilios usados: {h.domicilios.join(' · ')}</p>}
                </>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );

  return (
    <Layout hideSidebar={extras && wide}>
      {dialog}
      {dialogConfirmar}
      {fichaPedido}
      <div className="w-full">
        <DeliveryNav right={
          <>
            <label className="relative">
              <Search size={15} className="pointer-events-none absolute left-2.5 top-2.5 text-content-3" />
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar folio, domicilio o cliente"
                className="w-64 rounded-lg border border-line bg-surface py-2 pl-8 pr-3 text-sm text-content" />
            </label>
            {extras && (
              <button type="button" onClick={alternarWide} title={wide ? 'Mostrar el menú lateral' : 'Pantalla amplia: ocultar el menú lateral'}
                className="hidden items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 py-2 text-sm text-content-2 hover:text-content md:flex">
                {wide ? <Minimize2 size={15} /> : <Maximize2 size={15} />}{wide ? 'Menú' : 'Amplia'}
              </button>
            )}
          </>
        } />

        {cerrado && (
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
            <Lock size={15} /> El corte de hoy ya se cerró. Para corregir, reábrelo desde «Corte del día».
          </div>
        )}

        {puedeOperar && !cerrado && board && (
          <form onSubmit={guardar} className="mb-4 rounded-2xl border border-line bg-surface p-3 shadow-sm">
            {layout === 'phone_first' ? (
              <>
                {/* Captura «teléfono primero» (flag form_layout, Cenizas 28-oct): el teléfono busca al cliente; abajo folio,
                    domicilio, total, envío (desplegable) y pago (desplegable), en el orden de su rutina. */}
                <div className="flex flex-wrap items-end gap-2">
                  <label className="w-52">
                    <span className="mb-0.5 block text-xs font-medium text-content-2">Teléfono {board.settings.phone_required ? '' : '(opcional)'}</span>
                    <input ref={phoneInput} autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="10 dígitos"
                      className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content" />
                  </label>
                  {cliente && <span className="pb-2.5 text-sm text-accent">{cliente.name}{cashbackOn ? ` · saldo ${money(cliente.balance)}` : ''}</span>}
                  {phone.replace(/\D/g, '').length === 10 && !cliente && (
                    <label className="w-40">
                      <span className="mb-0.5 block text-xs font-medium text-content-2">Nombre (cliente nuevo)</span>
                      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jessy"
                        className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content" />
                    </label>
                  )}
                  {pickupOn && (
                    <button type="button" onClick={() => setPickup(!pickup)} title="El cliente recoge en el local: sin zona, sin envío, sin repartidor" className={`ml-auto flex items-center gap-1 ${chip(pickup)}`}>
                      <Store size={14} />Pickup <span className="text-content-3">recoge en local</span>
                    </button>
                  )}
                </div>
                {confirmarDir && !pickup && sugeridos.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-medium text-content-2">Domicilio del cliente:</span>
                    {sugeridos.map((a) => (
                      <button type="button" key={a} onClick={() => elegirDomicilio(a)} className={chip(dirElegida === 'guardado' && address === a)}><MapPin size={12} className="mr-1 inline" />{a}</button>
                    ))}
                    <button type="button" onClick={domicilioNuevo} className={chip(dirElegida === 'nuevo')}>Domicilio nuevo</button>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <label className="w-28">
                    <span className="mb-0.5 block text-xs font-medium text-content-2">{label}</span>
                    <input ref={refInput} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="444"
                      className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 font-mono text-base text-content" />
                  </label>
                  <label className="min-w-[16rem] flex-1">
                    <span className="mb-0.5 block text-xs font-medium text-content-2">Domicilio{pickup ? ' (opcional, recoge en el local)' : ''}</span>
                    <input value={address} onChange={(e) => { setAddress(e.target.value); if (confirmarDir && dirElegida === 'none' && sugeridos.length) setDirElegida('nuevo'); }}
                      list={confirmarDir ? undefined : 'dlv-dirs'} placeholder="Calle, número, colonia, referencia"
                      className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content" />
                    {!confirmarDir && <datalist id="dlv-dirs">{sugeridos.map((a) => <option key={a} value={a} />)}</datalist>}
                  </label>
                  <label className="w-28">
                    <span className="mb-0.5 block text-xs font-medium text-content-2">Total</span>
                    <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="$415"
                      className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content" />
                  </label>
                  {!pickup && (
                    <label className="w-44">
                      <span className="mb-0.5 block text-xs font-medium text-content-2">Costo de envío</span>
                      <select value={envioValor} onChange={(e) => elegirEnvio(e.target.value)} className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content">
                        <option value="">Sin zona</option>
                        {board.zones.map((z) => <option key={z.id} value={String(z.id)}>{z.name} · {money(z.fee)}</option>)}
                        <option value="cortesia">Sin envío (cortesía)</option>
                        <option value="otro">Otro…</option>
                      </select>
                    </label>
                  )}
                  {!pickup && feeOtro && (
                    <label className="w-24">
                      <span className="mb-0.5 block text-xs font-medium text-content-2">Envío $</span>
                      <input autoFocus value={fee} onChange={(e) => setFee(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="$0"
                        className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content" />
                    </label>
                  )}
                  <label className="w-36">
                    <span className="mb-0.5 block text-xs font-medium text-content-2">Método de pago</span>
                    <select value={pay} onChange={(e) => setPay(e.target.value as Payment)} className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content">
                      {PAGOS.map((p) => <option key={p} value={p}>{PAY_LABEL[p]}</option>)}
                    </select>
                  </label>
                  <button type="submit" disabled={saving}
                    className="flex h-[42px] items-center gap-1.5 rounded-lg bg-accent-solid px-4 text-sm font-semibold text-white hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Guardar <span className="hidden text-white/70 lg:inline">(Enter)</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="w-28">
                    <span className="mb-0.5 block text-xs font-medium text-content-2">{label}</span>
                    <input ref={refInput} autoFocus value={ref} onChange={(e) => setRef(e.target.value)} placeholder="444"
                      className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 font-mono text-base text-content" />
                  </label>
                  <label className="w-28">
                    <span className="mb-0.5 block text-xs font-medium text-content-2">Monto</span>
                    <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="$415"
                      className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content" />
                  </label>
                  {!pickup && (
                    <div>
                      <span className="mb-0.5 block text-xs font-medium text-content-2">Zona</span>
                      <div className="flex flex-wrap gap-1">
                        {board.zones.map((z) => (
                          <button type="button" key={z.id} onClick={() => elegirZona(z.id)} className={chip(zoneId === z.id)}>
                            {z.name} <span className="text-content-3">{money(z.fee)}</span>
                          </button>
                        ))}
                        <button type="button" onClick={alternarCortesia} title="Familiares o envíos especiales: no se cobra envío y no genera comisión" className={chip(courtesy)}>
                          Sin envío <span className="text-content-3">cortesía</span>
                        </button>
                      </div>
                    </div>
                  )}
                  {!pickup && (
                    <label className="w-20">
                      <span className="mb-0.5 block text-xs font-medium text-content-2">Envío</span>
                      <input value={courtesy ? '0' : fee} disabled={courtesy} onChange={(e) => setFee(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="$0"
                        className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content disabled:opacity-60" />
                    </label>
                  )}
                  {pickupOn && (
                    <button type="button" onClick={() => setPickup(!pickup)} title="El cliente recoge en el local: sin zona, sin envío, sin repartidor" className={`flex items-center gap-1 self-end ${chip(pickup)}`}>
                      <Store size={14} />Pickup <span className="text-content-3">recoge en local</span>
                    </button>
                  )}
                  <div>
                    <span className="mb-0.5 block text-xs font-medium text-content-2">Pago</span>
                    <div className="flex flex-wrap gap-1">
                      {PAGOS.map((p) => <button type="button" key={p} onClick={() => setPay(p)} className={chip(pay === p)}>{PAY_LABEL[p]}</button>)}
                    </div>
                  </div>
                </div>
                {confirmarDir && !pickup && sugeridos.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-medium text-content-2">Domicilio del cliente:</span>
                    {sugeridos.map((a) => (
                      <button type="button" key={a} onClick={() => elegirDomicilio(a)} className={chip(dirElegida === 'guardado' && address === a)}><MapPin size={12} className="mr-1 inline" />{a}</button>
                    ))}
                    <button type="button" onClick={domicilioNuevo} className={chip(dirElegida === 'nuevo')}>Domicilio nuevo</button>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <label className="w-40">
                    <span className="mb-0.5 block text-xs font-medium text-content-2">Teléfono {board.settings.phone_required ? '' : '(opcional)'}</span>
                    <input ref={phoneInput} value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="10 dígitos"
                      className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content" />
                  </label>
                  {phone.replace(/\D/g, '').length === 10 && !cliente && (
                    <label className="w-40">
                      <span className="mb-0.5 block text-xs font-medium text-content-2">Nombre (cliente nuevo)</span>
                      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jessy"
                        className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content" />
                    </label>
                  )}
                  <label className="min-w-[16rem] flex-1">
                    <span className="mb-0.5 block text-xs font-medium text-content-2">
                      Domicilio{pickup ? ' (opcional, recoge en el local)' : ''} {cliente && <span className="text-accent">· {cliente.name}{cashbackOn ? `, saldo ${money(cliente.balance)}` : ''}</span>}
                    </span>
                    <input value={address} onChange={(e) => { setAddress(e.target.value); if (confirmarDir && dirElegida === 'none' && sugeridos.length) setDirElegida('nuevo'); }}
                      list={confirmarDir ? undefined : 'dlv-dirs'} placeholder="Calle, número, colonia, referencia"
                      className="w-full rounded-lg border border-line bg-surface px-2.5 py-2 text-base text-content" />
                    {!confirmarDir && <datalist id="dlv-dirs">{sugeridos.map((a) => <option key={a} value={a} />)}</datalist>}
                  </label>
                  <button type="submit" disabled={saving}
                    className="flex h-[42px] items-center gap-1.5 rounded-lg bg-accent-solid px-4 text-sm font-semibold text-white hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Guardar <span className="hidden text-white/70 lg:inline">(Enter)</span>
                  </button>
                </div>
              </>
            )}
            {ultimo && <p className="mt-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">{ultimo}</p>}
          </form>
        )}

        {puedeOperar && !cerrado && board && board.couriers.some((c) => c.active) && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            <span className="mr-1 flex items-center gap-1 text-xs font-medium text-content-2"><Wallet size={13} /> Fondo de cambio de hoy:</span>
            {board.couriers.filter((c) => c.active).map((c) => (
              <button type="button" key={c.id} onClick={() => capturarFondo(c)} title="Toca para capturar o cambiar el fondo"
                className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-xs text-content hover:border-accent">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: c.color || '#94a3b8' }} />{c.name}
                <span className={fondos[c.id] ? 'font-semibold' : 'text-content-3'}>{fondos[c.id] ? money(fondos[c.id]) : 'sin fondo'}</span>
              </button>
            ))}
            <span className="text-xs text-content-3">Toca al repartidor para capturarlo (también en la columna Fondo del Corte del día).</span>
          </div>
        )}

        {!board ? (
          <div className="flex justify-center py-20 text-content-3"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            <div className="grid gap-3 lg:grid-cols-3">
              {columna('Listos para salir', <PackageCheck size={16} />, filtra(board.listos), 'Sin pedidos esperando', 'text-amber-600')}
              {columna('En camino', <Bike size={16} />, filtra(board.en_camino), 'Nadie en ruta', 'text-accent')}
              {columna('Entregados hoy', <Check size={16} />, filtra(board.entregados), 'Todavía ninguno', 'text-emerald-600')}
            </div>
            <p className="mt-3 text-sm text-content-2">
              Hoy: {board.en_camino.length + board.entregados.length} pedidos con repartidor · {money(totalHoy)}
              {board.cancelados.length ? ` · ${board.cancelados.length} cancelado(s)` : ''}
              {couriers.length === 0 && ' · Aún no hay repartidores: agrégalos en Configuración.'}
            </p>
          </>
        )}
      </div>
    </Layout>
  );
}
